import { useCallback, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useConsumersStore } from '@src/stores';

// Shared visibility count across all hook instances
const visibilityCount = new Map<string, number>();

const useConsumerControl = (consumerId?: string) => {
  const { consumers } = useConsumersStore(
    useShallow((state) => ({
      consumers: state.consumers,
    }))
  );

  // Track if this instance has registered as visible
  const isRegisteredRef = useRef(false);

  const handleVisibilityChange = useCallback(
    async (isVisible: boolean) => {
      if (!consumerId) return;

      try {
        const consumerPromise = consumers.get(consumerId);
        if (!consumerPromise) return;

        const consumer = await consumerPromise;

        if (isVisible) {
          if (!isRegisteredRef.current) {
            isRegisteredRef.current = true;
            visibilityCount.set(consumerId, (visibilityCount.get(consumerId) || 0) + 1);
          }
          if (consumer.paused) {
            consumer.resume();
          }
        } else if (isRegisteredRef.current) {
          isRegisteredRef.current = false;
          const newCount = (visibilityCount.get(consumerId) || 1) - 1;
          if (newCount <= 0) {
            visibilityCount.delete(consumerId);
            if (!consumer.paused) {
              consumer.pause();
            }
          } else {
            visibilityCount.set(consumerId, newCount);
          }
        }
      } catch (error) {
        console.error('Error controlling consumer visibility:', error);
      }
    },
    [consumerId, consumers]
  );

  return { handleVisibilityChange };
};

export default useConsumerControl;
